import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import {
  Landmark,
  Building2,
  HardHat,
  Map,
  Cpu,
  Sparkles,
  Users,
  Lightbulb,
  Target,
  DraftingCompass,
  Zap,
  TrainFront,
  Briefcase,
  LineChart,
  Handshake,
  Globe2,
} from "lucide-react";
import {
  SA_PRIMARY,
  SA_SECONDARY,
  saReadableText,
  saGradientLine,
  saDarkCardBase,
  onDarkCardEnter,
  onDarkCardLeave,
} from "./saSectionTokens";

const WHO_SHOULD_ATTEND = [
  { label: "Government Ministries & Municipalities", icon: Landmark, color: SA_PRIMARY },
  { label: "Giga Project Developers", icon: Sparkles, color: SA_SECONDARY },
  { label: "Infrastructure Authorities", icon: Building2, color: SA_PRIMARY },
  { label: "Real Estate Developers", icon: Building2, color: SA_SECONDARY },
  { label: "Utilities & Energy Companies", icon: Zap, color: SA_PRIMARY },
  { label: "Transport Authorities", icon: TrainFront, color: SA_SECONDARY },
  { label: "Engineering & Construction Firms", icon: HardHat, color: SA_PRIMARY },
  { label: "Digital Engineering & BIM Leaders", icon: DraftingCompass, color: SA_SECONDARY },
  { label: "GIS & Geospatial Professionals", icon: Map, color: SA_PRIMARY },
  { label: "Technology Providers", icon: Cpu, color: SA_SECONDARY },
  { label: "Investors & Consultants", icon: Briefcase, color: SA_PRIMARY },
];

const CONFERENCE_HIGHLIGHTS = [
  "Government digital transformation initiatives",
  "Digital Twin implementation strategies",
  "AI-powered infrastructure management",
  "Smart city operating models",
  "BIM, GIS & Digital Engineering integration",
  "Digital asset lifecycle management",
  "Sustainability and Net Zero initiatives",
  "Future-ready infrastructure investment",
];

function AttendeeRow({ label, icon: Icon, color }) {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;
  return (
    <Card
      className="mb-2"
      style={{
        ...saDarkCardBase,
        borderRadius: "12px",
        padding: size ? "0.95rem 1.15rem" : "0.85rem 1rem",
        transition: "all 0.35s ease",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        onDarkCardEnter(e, color);
        e.currentTarget.style.transform = "translateX(4px)";
      }}
      onMouseLeave={(e) => {
        onDarkCardLeave(e);
        e.currentTarget.style.transform = "translateX(0)";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
        <div
          style={{
            width: 38,
            height: 38,
            minWidth: 38,
            borderRadius: "10px",
            background: `${color}26`,
            border: `1px solid ${color}55`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={19} strokeWidth={2} style={{ color }} />
        </div>
        <span
          className="text-white"
          style={{
            fontSize: size ? "0.95rem" : "0.88rem",
            fontWeight: 600,
            lineHeight: 1.35,
            ...saReadableText,
          }}
        >
          {label}
        </span>
      </div>
    </Card>
  );
}

function SAWhoWhyAttendSection() {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

  const highlightIcons = [Lightbulb, Cpu, LineChart, Globe2, DraftingCompass, Target, Sparkles, Handshake];

  return (
    <section className="section py-5" style={{ background: "transparent", position: "relative", zIndex: 1 }}>
      <Container>
        <Row className="justify-content-center mb-4">
          <Col className="text-center">
            <h2
              className="mb-0"
              style={{
                fontSize: size ? "2.25rem" : "1.85rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
                lineHeight: 1.15,
                textTransform: "uppercase",
                color: SA_PRIMARY,
                ...saReadableText,
              }}
            >
              Who Should Attend?
            </h2>
            <div
              style={{
                width: "110px",
                height: "4px",
                borderRadius: "2px",
                margin: "1rem auto 1.75rem",
                ...saGradientLine,
              }}
            />
          </Col>
        </Row>
        <Row>
          {WHO_SHOULD_ATTEND.map((item, i) => (
            <Col md={6} key={i} className="mb-2">
              <AttendeeRow label={item.label} icon={item.icon} color={item.color} />
            </Col>
          ))}
        </Row>

        <Row className="mt-5">
          <Col>
            <h2
              className="mb-3"
              style={{
                fontSize: size ? "2rem" : "1.55rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: SA_SECONDARY,
                ...saReadableText,
              }}
            >
              Conference Highlights
            </h2>
            <p
              className="mb-3"
              style={{
                fontSize: size ? "1.05rem" : "0.98rem",
                fontWeight: 600,
                ...saReadableText,
              }}
            >
              Learn from Industry Leaders
            </p>
            <div
              style={{
                width: "72px",
                height: "4px",
                borderRadius: "2px",
                marginBottom: "1.35rem",
                ...saGradientLine,
              }}
            />
          </Col>
        </Row>
        <Row>
          {CONFERENCE_HIGHLIGHTS.map((line, i) => {
            const Icon = highlightIcons[i] || Users;
            return (
              <Col md={6} key={i} className="mb-3">
                <Card
                  style={{
                    ...saDarkCardBase,
                    borderRadius: "14px",
                    padding: size ? "1.1rem 1.25rem" : "1rem 1.1rem",
                    height: "100%",
                    transition: "all 0.35s ease",
                  }}
                  onMouseEnter={(e) => {
                    const c = i % 2 === 0 ? SA_PRIMARY : SA_SECONDARY;
                    onDarkCardEnter(e, c);
                    e.currentTarget.style.transform = "translateY(-3px)";
                  }}
                  onMouseLeave={(e) => {
                    onDarkCardLeave(e);
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.9rem" }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        minWidth: 44,
                        borderRadius: "12px",
                        background: `linear-gradient(145deg, ${SA_PRIMARY}28, ${SA_SECONDARY}18)`,
                        border: `2px solid ${SA_PRIMARY}45`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon size={22} strokeWidth={2} style={{ color: SA_SECONDARY }} />
                    </div>
                    <span
                      className="text-white"
                      style={{
                        fontSize: size ? "0.98rem" : "0.9rem",
                        fontWeight: 600,
                        lineHeight: 1.45,
                        ...saReadableText,
                      }}
                    >
                      {line}
                    </span>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </section>
  );
}

export default SAWhoWhyAttendSection;
